import React, { useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQuery, useQueryClient } from "react-query";
import MomentDatePicker from "../../../../../components/inputs/MomentDatePicker";
import { errorText, inr, payrollApi, ROLE_LABEL } from "./payrollApi";
import {
  card,
  centeredModal,
  FormHeading,
  GAP,
  iconBox,
  iconButton,
  Toolbar,
} from "./layout";

const GROUP_OPTIONS = [
  { value: "all", label: "All staff" },
  { value: "instructor", label: "Instructors" },
  { value: "support", label: "Drivers & support staff" },
];

const ComponentList = ({ name, addLabel }) => (
  <Form.List name={name}>
    {(fields, { add, remove }) => (
      <>
        {fields.map(({ key, name: n, ...rest }) => (
          <Space key={key} align="start" size={GAP.sm} style={{ display: "flex" }}>
            <Form.Item
              {...rest}
              name={[n, "name"]}
              rules={[{ required: true, message: "Name" }]}
            >
              <Input placeholder="Name" />
            </Form.Item>
            <Form.Item
              {...rest}
              name={[n, "amount"]}
              rules={[{ required: true, message: "Amount" }]}
            >
              <InputNumber min={0} placeholder="Amount" />
            </Form.Item>
            <span
              role="button"
              aria-label="Remove"
              title="Remove"
              style={{ ...iconBox, color: "#9ca3af" }}
              onClick={() => remove(n)}
            >
              <MinusCircleOutlined />
            </span>
          </Space>
        ))}
        <Button type="dashed" style={iconButton} onClick={() => add()}>
          <PlusOutlined />
          <span>{addLabel}</span>
        </Button>
      </>
    )}
  </Form.List>
);

const SalaryProfiles = () => {
  const queryClient = useQueryClient();
  const [group, setGroup] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const { data: staff = [], isLoading } = useQuery(
    ["payroll-staff", group],
    () => payrollApi.staff(group === "all" ? undefined : group)
  );

  const open = (row) => {
    const p = row.profile;
    form.resetFields();
    form.setFieldsValue({
      base_salary: p?.base_salary,
      allowances: p?.allowances || [],
      fixed_deductions: p?.fixed_deductions || [],
      overtime_enabled: !!p?.overtime_enabled,
      ot_rate: p?.ot_rate || 0,
      effective_from: p?.effective_from ? moment(p.effective_from) : moment().startOf("month"),
      end_date: p?.end_date ? moment(p.end_date) : null,
      status: p?.status || "active",
    });
    setEditing(row);
  };

  const save = async () => {
    const v = await form.validateFields();
    try {
      setSaving(true);
      await payrollApi.saveProfile({
        user: editing.user_id,
        base_salary: v.base_salary,
        allowances: v.allowances || [],
        fixed_deductions: v.fixed_deductions || [],
        overtime_enabled: !!v.overtime_enabled,
        ot_rate: v.overtime_enabled ? v.ot_rate || 0 : 0,
        effective_from: v.effective_from.format("YYYY-MM-DD"),
        end_date: v.end_date ? v.end_date.format("YYYY-MM-DD") : undefined,
        status: v.status,
      });
      message.success("Salary profile saved");
      setEditing(null);
      queryClient.invalidateQueries("payroll-staff");
    } catch (err) {
      message.error(errorText(err, "Could not save salary profile"));
    } finally {
      setSaving(false);
    }
  };

  const rows = staff.filter((s) =>
    s.name.toLowerCase().includes(search.trim().toLowerCase())
  );
  const missing = staff.filter((s) => !s.profile).length;

  const columns = [
    {
      title: "Staff",
      dataIndex: "name",
      render: (v, r) => (
        <div>
          <div className="font-medium">{v}</div>
          <div className="text-xs text-gray-500">
            {r.designation || ROLE_LABEL[r.role] || r.role}
          </div>
        </div>
      ),
    },
    { title: "Role", dataIndex: "role", render: (v) => ROLE_LABEL[v] || v },
    {
      title: "Base salary",
      align: "right",
      render: (_, r) => (r.profile ? inr(r.profile.base_salary) : "—"),
    },
    {
      title: "Allowances",
      align: "right",
      render: (_, r) =>
        r.profile
          ? inr((r.profile.allowances || []).reduce((s, a) => s + a.amount, 0))
          : "—",
    },
    {
      title: "Effective from",
      render: (_, r) =>
        r.profile ? moment(r.profile.effective_from).format("DD MMM YYYY") : "—",
    },
    {
      title: "Status",
      render: (_, r) =>
        !r.profile ? (
          <Tag color="red">Not set up</Tag>
        ) : r.profile.status === "inactive" ? (
          <Tag>Inactive</Tag>
        ) : (
          <Tag color="green">Active</Tag>
        ),
    },
    {
      title: "",
      align: "right",
      render: (_, r) => (
        <Button size="small" onClick={() => open(r)}>
          {r.profile ? "Edit" : "Set up"}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Toolbar
        left={
          <>
            <Select
              value={group}
              onChange={setGroup}
              options={GROUP_OPTIONS}
              style={{ width: 220 }}
            />
            <Input.Search
              allowClear
              placeholder="Search staff"
              style={{ width: 260 }}
              onChange={(e) => setSearch(e.target.value)}
            />
          </>
        }
        right={
          missing > 0 ? (
            <Tag color="red" style={{ margin: 0 }}>
              {missing} without a salary profile
            </Tag>
          ) : null
        }
      />

      <div style={{ ...card, padding: GAP.sm }}>
        <Table
          rowKey="user_id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        {...centeredModal}
        open={!!editing}
        width={600}
        title={editing ? `Salary — ${editing.name}` : ""}
        onCancel={() => setEditing(null)}
        onOk={save}
        okText="Save"
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="base_salary"
            label="Monthly base salary (₹)"
            rules={[{ required: true, message: "Enter the monthly salary" }]}
          >
            <InputNumber min={0} className="w-full" />
          </Form.Item>

          <Form.Item
            name="effective_from"
            label="Effective from"
            extra="A new date creates a new version, so earlier months keep their old salary."
            rules={[{ required: true }]}
          >
            <MomentDatePicker style={{ width: "100%" }} allowClear={false} />
          </Form.Item>

          <FormHeading>Allowances</FormHeading>
          <ComponentList name="allowances" addLabel="Add allowance" />

          <FormHeading>Fixed deductions</FormHeading>
          <ComponentList name="fixed_deductions" addLabel="Add deduction (PF, tax…)" />

          <div style={{ marginTop: GAP.xl }}>
            <Form.Item
              name="overtime_enabled"
              label="Pay overtime"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(a, b) => a.overtime_enabled !== b.overtime_enabled}
            >
              {({ getFieldValue }) =>
                getFieldValue("overtime_enabled") ? (
                  <Form.Item name="ot_rate" label="Overtime rate (₹ per hour)">
                    <InputNumber min={0} className="w-full" />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </div>

          <Form.Item
            name="end_date"
            label="Last working day (optional)"
            extra="Set when someone leaves; later days are not paid."
          >
            <MomentDatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="status" label="Status">
            <Select
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive (exclude from payroll)" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SalaryProfiles;
